import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import OneSignalService, { type SubscriptionState } from '@/services/OneSignalService';
import { registerPushState } from '@/integrations/analytics/sessionContext';

export const AuthenticatedPushManager: React.FC = () => {
  const { user } = useAuth();
  const hasInitializedRef = useRef(false);
  const hasAutoResubscribedRef = useRef(false);

  useEffect(() => {
    if (hasInitializedRef.current) return;

    const initializeOneSignal = async () => {
      try {
        const appId = import.meta.env.VITE_ONESIGNAL_APP_ID_DEV || import.meta.env.VITE_ONESIGNAL_APP_ID;
        if (!appId) return;

        if (window.OneSignal) {
          await OneSignalService.initialize(appId);
          hasInitializedRef.current = true;
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
        script.defer = true;
        script.onload = async () => {
          await new Promise(resolve => setTimeout(resolve, 500));
          await OneSignalService.initialize(appId);
          hasInitializedRef.current = true;
        };
        script.onerror = () => {
          console.error('[PushManager] Failed to load OneSignal SDK');
        };
        document.head.appendChild(script);
      } catch (error) {
        console.error('[PushManager] Error initializing OneSignal:', error);
      }
    };

    initializeOneSignal();
  }, []);

  useEffect(() => {
    if (user?.id) {
      OneSignalService.setUserId(user.id);
      setTimeout(() => OneSignalService.checkSubscriptionState(), 1000);
    } else {
      hasAutoResubscribedRef.current = false;
    }
  }, [user?.id]);

  // Keep the push super properties current. push_permission is tracked for
  // every session; OneSignal reports push_subscribed only once a user is set.
  useEffect(() => OneSignalService.onSubscriptionChange(registerPushState), []);

  useEffect(() => {
    if (!user?.id) return;

    return OneSignalService.onSubscriptionChange((state: SubscriptionState) => {
      if (
        state.isInitialized &&
        !state.isSubscribed &&
        !state.error &&
        state.browserPermission === 'granted' &&
        !hasAutoResubscribedRef.current
      ) {
        hasAutoResubscribedRef.current = true;
        OneSignalService.subscribe().catch(console.error);
      }
    });
  }, [user?.id]);

  return null;
};
